import React from 'react'
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldError,
  RegisterOptions,
} from 'react-hook-form'
import { mapHourSeriesTypeToEnergyType } from '../../calculators/EnergyFlow'
import {
  HourSeriesCollectionFragment,
  HourSeriesFragment,
  HourSeriesType,
} from '../../generated/graphql'
import {
  MultiPicker,
  PickerCollection,
  PickerSubCollection,
} from './MultiPicker'

type Props = {
  collections: HourSeriesCollectionFragment[]
  control: Control
  name: string
  defaultValue?: string
  rules?: RegisterOptions
  error?: FieldError
}

export function ControlledMultiHourSeriesPicker(
  props: Props,
): React.ReactElement {
  const {
    control,
    name,
    defaultValue,
    rules,
    error,
    collections,
    ...inputProps
  } = props

  const filterHourSeriesToDemands = (hourSeries: HourSeriesFragment) =>
    [
      HourSeriesType.HeatingDemand,
      HourSeriesType.CoolingDemand,
      HourSeriesType.ElectricityDemand,
    ].includes(hourSeries.type)

  const pickerCollections: PickerCollection[] = collections.map(
    (hourSeriesCollection) => ({
      id: hourSeriesCollection.id,
      name: hourSeriesCollection.name,
      subCollections: hourSeriesCollection.hourSeries
        .filter(filterHourSeriesToDemands)
        .map(
          (hourSeries): PickerSubCollection => ({
            id: hourSeries.id,
            name: hourSeries.name,
            type: mapHourSeriesTypeToEnergyType(hourSeries.type),
          }),
        ),
    }),
  )

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue ?? []}
      rules={rules}
      render={({
        value,
        onChange,
      }: Omit<ControllerRenderProps, 'value'> & { value: string[] }) => (
        <MultiPicker
          {...inputProps}
          collections={pickerCollections}
          name={name}
          value={value}
          onChange={(newValue) => onChange(newValue)}
          error={error?.message}
        />
      )}
    />
  )
}
